import { PlatformLeadStageEnum } from './platform-lead-stage.enum';

export enum PlatformLeadSourceEnum {
  Signup = 'signup',
  Manual = 'manual',
  Referral = 'referral',
  Marketing = 'marketing',
  Cold = 'cold',
  Inbound = 'inbound',
  WhatsApp = 'whatsapp',
  Event = 'event',
}

export enum PlatformLeadPriorityEnum {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export enum PlatformLeadActivityTypeEnum {
  Note = 'note',
  Call = 'call',
  Whatsapp = 'whatsapp',
  Email = 'email',
  Meeting = 'meeting',
  System = 'system',
}

/**
 * How a contact attempt ended. Recorded per activity so "tried twice, never
 * answered" is visible without reading the notes.
 */
export enum PlatformLeadOutcomeEnum {
  Answered = 'answered',
  NoAnswer = 'no_answer',
  WrongNumber = 'wrong_number',
  CallbackLater = 'callback_later',
}

export interface IPlatformLeadNote {
  text: string;
  type?: PlatformLeadActivityTypeEnum;
  outcome?: PlatformLeadOutcomeEnum;
  author?: string;
  createdAt: Date | string;
}

export interface IPlatformLead {
  _id?: string;
  bid?: string;
  businessName: string;
  contactName?: string;
  phone: string;
  email: string;
  source?: PlatformLeadSourceEnum;
  priority?: PlatformLeadPriorityEnum;
  expectedValue?: number;
  tags?: string[];
  stage: PlatformLeadStageEnum;
  notes: IPlatformLeadNote[];
  lossReason?: string;
  requestedFeatures: string[];
  /** Email of the partner handling this lead */
  assignedTo?: string;
  lastContactedAt?: Date | string;
  /** Outcome of the most recent contact attempt */
  lastOutcome?: PlatformLeadOutcomeEnum;
  /** Consecutive unanswered contact attempts, reset on any answered contact */
  noAnswerCount?: number;
  nextFollowUpAt?: Date | string;
  signupDate?: Date | string;
  isTestBusiness?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** Live numbers pulled from the registered business a lead is linked to */
export interface IPlatformLeadBusinessStats {
  bid: string;
  title: string;
  plan?: string;
  contractStatus?: string;
  trialEndDate?: Date | string;
  /** Services the business has set up */
  servicesCount?: number;
  /** Past, non-cancelled appointments */
  appointmentsTotal: number;
  appointmentsLast30d: number;
  upcomingAppointments: number;
  lastAppointmentDate?: Date | string;
  createdAt?: Date | string;
}

/** A lead plus the stats of its linked business, when it has one */
export interface IPlatformLeadWithStats extends IPlatformLead {
  business?: IPlatformLeadBusinessStats;
  /** Nearest open task on this lead, so the list can show it without a second call */
  nextTask?: { _id: string; title: string; dueDate: Date | string };
  /** Count of open tasks on this lead */
  openTasksCount?: number;
}

/**
 * Everything one conversation produces, sent in a single request so logging a
 * call is one save rather than four.
 */
export interface IPlatformLeadLogDto {
  note?: string;
  type?: PlatformLeadActivityTypeEnum;
  outcome?: PlatformLeadOutcomeEnum;
  stage?: PlatformLeadStageEnum;
  requestedFeatures?: string[];
  priority?: PlatformLeadPriorityEnum;
  assignedTo?: string;
  author?: string;
  /** When set, creates a task for the operator and stores it as the next follow-up */
  task?: {
    title: string;
    dueDate: Date | string;
    notes?: string;
  };
}

export interface IPlatformLeadBulkUpdateDto {
  ids: string[];
  stage?: PlatformLeadStageEnum;
  assignedTo?: string;
  priority?: PlatformLeadPriorityEnum;
}

export interface IPlatformFeaturePopularity {
  feature: string;
  count: number;
  businesses: string[];
}

export interface IPlatformPipelineStageSummary {
  stage: PlatformLeadStageEnum;
  count: number;
  expectedValue: number;
}

export interface IPlatformPipelineSummary {
  byStage: IPlatformPipelineStageSummary[];
  /** Leads in new or in-progress */
  totalOpen: number;
  /** Never contacted at all — the backlog to work through */
  neverContacted: number;
  staleCount: number;
  contactedThisWeek: number;
  newThisWeek: number;
  pipelineValue: number;
  /** Leads marked done */
  convertedCount: number;
  /** Done out of everything closed (done/cold/spam), in percent */
  conversionRate: number;
}
